const BASE_URL = '/api/v1';
const TOKEN_KEY = 'pmw-click-frontend-token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(status, code, message) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request(path, { method = 'GET', body, params } = {}) {
  let url = BASE_URL + path;
  if (params) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    if (qs) url += '?' + qs;
  }

  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const json = await res.json().catch(() => null);

  if (res.status === 401) {
    setToken(null);
    window.dispatchEvent(new Event('pmw-click-frontend:unauthorized'));
  }

  if (!res.ok || !json?.success) {
    throw new ApiError(res.status, json?.error || 'UNKNOWN_ERROR', json?.message || 'Request failed');
  }

  return { data: json.data, meta: json.meta };
}

export const api = {
  get: (path, params) => request(path, { method: 'GET', params }).then((r) => r.data),
  getWithMeta: (path, params) => request(path, { method: 'GET', params }),
  post: (path, body) => request(path, { method: 'POST', body }).then((r) => r.data),
  put: (path, body) => request(path, { method: 'PUT', body }).then((r) => r.data),
  del: (path) => request(path, { method: 'DELETE' }).then((r) => r.data),
};

// Authenticated file download (e.g. a CSV export) — fetch() rather than a
// plain <a href>, since the admin JWT rides in a header, not a cookie.
// Uses the server's Content-Disposition filename when it sends one.
export async function downloadFile(path, params, fallbackName = 'download') {
  let url = BASE_URL + path;
  if (params) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    if (qs) url += '?' + qs;
  }
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url, { headers });
  if (res.status === 401) {
    setToken(null);
    window.dispatchEvent(new Event('pmw-click-frontend:unauthorized'));
  }
  if (!res.ok) {
    const json = await res.json().catch(() => null);
    throw new ApiError(res.status, json?.error || 'UNKNOWN_ERROR', json?.message || 'Download failed');
  }

  const blob = await res.blob();
  const match = /filename="?([^"]+)"?/.exec(res.headers.get('Content-Disposition') || '');
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = match ? match[1] : fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}
